import React, { useEffect, useState } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import Logger from "../../services/Logger";
import CrmTabs from "../crm/CrmTabs";
import { adminCrmFeedbackDetailPath } from "../crm/crmRoutes";
import AdminFeedbackService from "./AdminFeedbackService";
import { getFeedbackColumns } from "./getFeedbackColumns";

const SORT_OPTIONS = [
    { value: "createdAt:desc", label: "Newest first" },
    { value: "createdAt:asc", label: "Oldest first" }
];

export default function AdminFeedbackMain() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [totalRows, setTotalRows] = useState(0);
    const [sort, setSort] = useState("createdAt:desc");
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const fetchList = async () => {
        setLoading(true);
        const result = await AdminFeedbackService.list(paginationModel.page, paginationModel.pageSize, sort);
        setLoading(false);
        if (result.success && result.data) {
            const data = result.data;
            const content = data.content ?? data;
            const list = Array.isArray(content) ? content : [];
            setRows(list);
            const total = data.page?.totalElements ?? data.totalElements ?? list.length;
            setTotalRows(typeof total === "number" ? total : 0);
            if (list.length > 0 && data.page?.totalElements == null && data.totalElements == null) {
                Logger.warn("Feedback API response may be missing page.totalElements:", data);
            }
        } else {
            setRows([]);
            setTotalRows(0);
            setSnackbar({ open: true, severity: "error", message: result.error || "Failed to load feedback" });
        }
    };

    useEffect(() => {
        fetchList();
    }, [paginationModel.page, paginationModel.pageSize, sort]);

    const handleRowClick = (params) => {
        if (params.row?.id) {
            navigate(adminCrmFeedbackDetailPath(params.row.id));
        }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <CrmTabs />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h5">Feedback</Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel id="feedback-sort-label">Sort</InputLabel>
                    <Select
                        labelId="feedback-sort-label"
                        label="Sort"
                        value={sort}
                        onChange={(e) => {
                            setSort(e.target.value);
                            setPaginationModel((prev) => ({ ...prev, page: 0 }));
                        }}
                    >
                        {SORT_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                                {o.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ width: "100%", minHeight: 400 }}>
                <DataGrid
                    rows={rows}
                    columns={getFeedbackColumns()}
                    getRowId={(row) => row.id}
                    loading={loading}
                    paginationMode="server"
                    rowCount={totalRows}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                    onRowClick={handleRowClick}
                    autoHeight
                    sx={{ minHeight: 400, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                />
            </Box>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(open) => setSnackbar((s) => ({ ...s, open }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Box>
    );
}
